// Dados vindos da API. Toda escrita invalida o que depende dela, e a tela se atualiza sozinha.
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api.ts';

export const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, refetchOnWindowFocus: true, retry: 1 } },
});

export type Status = 'pending' | 'confirmed' | 'arrived' | 'in_service' | 'completed' | 'no_show' | 'cancelled';
export type DepositStatus = 'none' | 'awaiting' | 'received' | 'credited' | 'retained' | 'applied' | 'refunded';

export interface DayHours { on: boolean; ini: string; fim: string }
export interface Settings {
  toleranceMin: number; cancelWindowHours: number; minAdvanceHours: number; maxAdvanceDays: number;
  deposit: { mode: 'never' | 'after_no_shows' | 'always'; linkAlways: boolean; afterNoShows: number; kind: 'pct' | 'fixed'; bps: number; fixedCents: number; deadlineHours: number; retainedSplit: 'split' | 'salon_only' };
  pixKey: string; feeMode: 'salon' | 'before_split'; laterLimitCents: number;
  hours: Record<'dom' | 'seg' | 'ter' | 'qua' | 'qui' | 'sex' | 'sab', DayHours>;
  lunch: DayHours; holidays: boolean; onlineBooking?: boolean; leanMenu?: boolean; allowOverlap?: boolean; hasReception?: boolean;
  profile: { cnpj: string; address: string; whatsapp: string; instagram: string; google: string };
  installments: { on: boolean; max: number; interest: 'salon' | 'client' };
  methods: { pix: boolean; cash: boolean; debit: boolean; credit: boolean; later: boolean };
  messages: Record<string, { on: boolean; text: string }>;
  loyalty: { on: boolean; visits: number; months: number; rewardLabel: string; rewardCents: number };
  packageRules: Array<{ sessions: number; offBps: number; days: number }>;
  giftCardMonths: number;
}
export interface Me {
  user: { id: string; name: string; email: string };
  role: string;
  permissions: string[];
  professionalId: string | null;
  salon: { id: string; name: string; slug: string; settings: Settings };
  features?: { email: boolean };
}
export interface Service {
  id: string; name: string; category: string; description: string; durationMin: number; cleanupMin: number; priceCents: number;
  priceFrom: boolean; returnDays: number; bookableOnline: boolean; active: boolean; version: number; professionalIds: string[];
}
export interface Professional {
  id: string; name: string; role: string; color: string; active: boolean; isOwner?: boolean; version?: number;
  cota?: { kind: 'fixed'; bps: number } | { kind: 'tiered'; tiers: Array<{ upToCents: number | null; bps: number }> };
  workHours?: Record<string, DayHours>;
}
export interface AgendaItem {
  id: string; date: string; time: string; startsAt: string; endsAt: string; status: Status; source: 'internal' | 'public' | 'import';
  priceCents: number; notes: string; depositCents: number; depositStatus: DepositStatus; depositDueAt: string | null;
  arrivedAt: string | null; lateMin: number | null; lateNoticeMin?: number | null; startedAt?: string | null; version: number; professionalId: string;
  clientId: string; client: string; phone: string; serviceId: string; service: string; seriesId: string | null; groupId: string | null;
}
export interface ClientRow {
  id: string; name: string; phone: string; birthday: string | null; whatsapp: boolean; version: number; credit: number; debt: number;
  last_visit: string | null; visits: number; has_future: boolean; last_service: string | null; last_pro: string | null; next_visit: string | null; return_days: number; days_away: number | null;
}
export interface ClientDetail {
  id: string; name: string; phoneDisplay: string; email: string; birthday: string | null; notes: string; instagram: string; origin: string;
  whatsappConsentAt: string | null; marketingConsentAt: string | null; photoConsent: { guardar?: string | null; publicar?: string | null }; version: number; credit: number; debt: number; createdAt: string;
  history: Array<{ id: string; startsAt: string; status: Status; priceCents: number; lateMin: number | null; service: string; serviceId: string; professional: string; professionalId: string }>;
  anamnesis: { id: string; answers: Record<string, unknown>; consentAt: string; createdAt: string; filledBy: string } | null;
  anamnesisReviewedAt: string | null;
}
export interface Machine { id: string; name: string; debitBps: number; creditBps: number; installmentBps: Record<string, number>; active: boolean }

export const can = (me: Me | undefined, perm: string) => !!me && (me.permissions.includes('*') || me.permissions.includes(perm));

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/me'), staleTime: 60_000, retry: (n, e) => !(e instanceof ApiError && e.status === 401) && n < 3 });
export const useServices = () => useQuery({ queryKey: ['services'], queryFn: () => api.get<Service[]>('/services'), staleTime: 60_000 });
export const usePros = () => useQuery({ queryKey: ['professionals'], queryFn: () => api.get<Professional[]>('/professionals'), staleTime: 60_000 });
export const useMachines = () => useQuery({ queryKey: ['machines'], queryFn: () => api.get<Machine[]>('/card-machines'), staleTime: 60_000 });
export const useAgenda = (from: string, to = from) =>
  useQuery({ queryKey: ['agenda', from, to], queryFn: () => api.get<AgendaItem[]>(`/agenda?from=${from}&to=${to}`), refetchInterval: 60_000 });
export const useClients = (q: string, filter: string) =>
  useQuery({ queryKey: ['clients', q, filter], queryFn: () => api.get<ClientRow[]>(`/clients?q=${encodeURIComponent(q)}&filter=${filter}`), placeholderData: (p) => p });
export const useClient = (id: string | undefined) =>
  useQuery({ queryKey: ['client', id], queryFn: () => api.get<ClientDetail>(`/clients/${id}`), enabled: !!id });
export const useAppt = (id: string) => useQuery({ queryKey: ['appt', id], queryFn: () => api.get<ApptDetail>(`/appointments/${id}`) });
export const useCash = (enabled = true) => useQuery({ queryKey: ['cash'], queryFn: () => api.get<CashSummary>('/cash/current'), enabled });
export const useDebts = () => useQuery({ queryKey: ['debts'], queryFn: () => api.get<Array<{ id: string; name: string; phone: string; debt: number; since: string }>>('/debts') });

export interface ApptDetail {
  id: string; date: string; time: string; endTime: string; startsAt: string; endsAt: string; status: Status; source: AgendaItem['source'];
  priceCents: number; notes: string; depositCents: number; depositStatus: DepositStatus; depositDueAt: string | null;
  arrivedAt: string | null; lateMin: number | null; lateNoticeMin?: number | null; version: number; professionalId: string; serviceId: string; clientId: string;
  cancelReason: string | null; startedAt: string | null; finishedAt: string | null; seriesId: string | null; groupId: string | null;
  client: { id: string; name: string; phone: string; whatsapp: boolean; notes: string };
  service: { id: string; name: string; durationMin: number; cleanupMin: number; priceCents: number };
  // procedimentos a mais no mesmo horário; priceCents é a soma e mainPriceCents só o principal
  serviceLabel: string;
  extras: Array<{ id: string; serviceId: string; name: string; priceCents: number; durationMin: number }>;
  mainPriceCents: number;
  professional: { id: string; name: string };
  order: null | { id: string; status: string; subtotalCents: number; discountCents: number; tipCents: number; totalCents: number; depositAppliedCents: number; feesCents: number; reverseReason: string | null };
  items: Array<{ description: string; quantity: number; totalCents: number; priceChangeReason: string | null }>;
  payments: Array<{ id: string; kind: string; method: string; amountCents: number; feeCents: number; installments: number }>;
  history: Array<{ action: string; actor: string; reason: string; at: string }>;
}
export interface CashSummary {
  session: null | { id: string; openedAt: string; openedBy: string; openingCents: number };
  byMethod?: Array<{ method: string; amount: number; fees: number; n: number }>;
  receivedCents?: number; feesCents?: number; supplyCents?: number; withdrawalCents?: number; expectedCashCents?: number;
  // Pix registrados ainda não batidos com o extrato
  pixPending?: Array<{ id: string; at: string; amount: number; kind: string; client: string | null }>; pixPendingCents?: number;
}

// invalida tudo o que uma escrita no dia a dia pode ter mudado
export function useRefresh() {
  const qc = useQueryClient();
  return () => Promise.all(['agenda', 'appt', 'client', 'clients', 'cash', 'debts', 'availability', 'wallet', 'products', 'gift-cards', 'client-packages', 'report', 'loyalty', 'photos', 'similar'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
}

export function useAction<T, R = unknown>(fn: (v: T) => Promise<R>) {
  const refresh = useRefresh();
  return useMutation({ mutationFn: fn, onSettled: () => refresh() });
}
